import axios from 'axios';

const API_URL = process.env.REACT_APP_USERS_API_URL || 'http://localhost:3001';
const STORAGE_KEY = 'adminuser';

// Dedicated client so the app-wide default-axios interceptors in utils.ts
// (which inject a `Platform` header + rewrite baseURL per selected vertical)
// never apply. QL-User-Backend's CORS allowlist excludes `Platform`, so that
// injected header made the refresh preflight fail on vertical pages (it worked
// on user pages only because those use the separate `api` instance). This
// instance also can't recurse into the `api` 401 interceptor.
const refreshHttp = axios.create();

interface AdminUser {
  token?: string;
  refresh_token?: string;
  [key: string]: any;
}

const readAdminUser = (): AdminUser | null => {
  const json = localStorage.getItem(STORAGE_KEY);
  if (!json) return null;
  try {
    return JSON.parse(json) as AdminUser;
  } catch {
    return null;
  }
};

export const getStoredToken = (): string | null =>
  readAdminUser()?.token ?? null;

export const getStoredRefreshToken = (): string | null =>
  readAdminUser()?.refresh_token ?? null;

// Single-flight: proactive timer + a 401 retry must share one refresh request,
// never fire two in parallel (which would rotate/race the tokens).
let refreshInFlight: Promise<string | null> | null = null;

const doRefresh = async (): Promise<string | null> => {
  const adminUser = readAdminUser();
  const refreshToken = adminUser?.refresh_token;
  if (!adminUser || !refreshToken) return null;

  try {
    // Plain axios (NOT the shared `api` instance) so the response interceptor
    // can't recurse into another refresh.
    const { data } = await refreshHttp.post(`${API_URL}/api/v4/refresh`, {
      refresh_token: refreshToken,
    });

    const newToken: string | undefined = data?.access_token;
    if (!newToken) return null;

    // Read-modify-write so we don't clobber other adminuser fields (user, roles).
    const latest = readAdminUser() ?? adminUser;
    latest.token = newToken;
    if (data?.refresh_token) latest.refresh_token = data.refresh_token;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(latest));

    return newToken;
  } catch {
    // Non-2xx (e.g. 401 expired/revoked refresh) or network error.
    return null;
  }
};

/**
 * Refresh the access token via POST /api/v4/refresh using the stored
 * `refresh_token`. Updates `localStorage.adminuser` and returns the new access
 * token, or null if refresh isn't possible (no/expired refresh token).
 * Single-flight: concurrent callers share one in-flight request.
 */
export const refreshAccessToken = (): Promise<string | null> => {
  if (refreshInFlight) return refreshInFlight;
  const p = (async () => {
    try {
      return await doRefresh();
    } finally {
      refreshInFlight = null;
    }
  })();
  refreshInFlight = p;
  return p;
};
